import { useRef, useState } from "react";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

const BrandLogo = ({
  brand,
  imageEditablePath,
  arrayEditablePath,
}: {
  brand: { name: string; logo: string; filter: string };
  imageEditablePath: string;
  arrayEditablePath: string;
}) => {
  const [imgError, setImgError] = useState(false);

  return (
    <div
      className="flex flex-col items-center justify-center gap-5 px-20 py-2 group cursor-default select-none flex-shrink-0 overflow-visible"
      {...editableAttrs(arrayEditablePath, "array")}
    >
      <div className="w-44 h-28 flex items-center justify-center overflow-visible">
        {!imgError ? (
          <img
            src={brand.logo}
            alt={brand.name}
            className="max-h-full max-w-full object-contain transition-all duration-300 will-change-transform group-hover:scale-105"
            style={{ filter: brand.filter, opacity: 0.45 }}
            {...editableAttrs(imageEditablePath, "image")}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = "1";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = "0.45";
            }}
            onError={() => setImgError(true)}
          />
        ) : (
          <span className="text-white/40 font-bold text-base tracking-widest uppercase group-hover:text-white/90 transition-colors duration-300" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {brand.name}
          </span>
        )}
      </div>
      <span className="text-zinc-500 text-sm tracking-widest uppercase group-hover:text-zinc-300 transition-colors duration-300 whitespace-nowrap" style={{ fontFamily: "Rajdhani, sans-serif" }}>
        {brand.name}
      </span>
    </div>
  );
};

const BrandsSection = () => {
  const trackRef = useRef<HTMLDivElement>(null);
  const [paused, setPaused] = useState(false);
  const { content } = useSiteContent();
  const section = content.home.brands;
  const marqueeBrands = [...section.logos, ...section.logos, ...section.logos];

  return (
    <section id="brands" className="relative bg-[#0a0a0a] overflow-hidden py-24" {...editableAttrs("home.brands", "object")}>
      <style>{`
        @keyframes brandMarquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.333%); }
        }
        .marquee-track { animation: brandMarquee 28s linear infinite; }
        .marquee-track.paused { animation-play-state: paused; }
      `}</style>

      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-red-600 to-transparent" />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "repeating-linear-gradient(0deg, #fff 0px, #fff 1px, transparent 1px, transparent 60px), repeating-linear-gradient(90deg, #fff 0px, #fff 1px, transparent 1px, transparent 60px)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-6">
        <div className="text-center mb-14">
          <div className="flex items-center justify-center gap-3 mb-5">
            <span className="w-10 h-px bg-red-600" />
            <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.brands.badge", "text")}>
              {section.badge}
            </span>
            <span className="w-10 h-px bg-red-600" />
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.brands.title", "text")}>
            {section.title}
          </h2>
          <p className="text-zinc-400 text-sm max-w-lg mx-auto leading-relaxed" {...editableAttrs("home.brands.description", "textarea")}>
            {section.description}
          </p>
        </div>
      </div>

      <div className="relative w-full overflow-hidden" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
        <div className="absolute left-0 top-0 h-full w-32 z-10 pointer-events-none" style={{ background: "linear-gradient(to right, #0a0a0a, transparent)" }} />
        <div className="absolute right-0 top-0 h-full w-32 z-10 pointer-events-none" style={{ background: "linear-gradient(to left, #0a0a0a, transparent)" }} />

        <div ref={trackRef} className={`marquee-track flex items-center${paused ? " paused" : ""}`} style={{ width: "max-content" }} {...editableAttrs("home.brands.logos", "array")}>
          {marqueeBrands.map((brand, i) => (
            <BrandLogo
              key={`${brand.name}-${i}`}
              brand={brand}
              imageEditablePath={`home.brands.logos.${i % Math.max(1, section.logos.length)}.logo`}
              arrayEditablePath="home.brands.logos"
            />
          ))}
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-6 mt-14">
        <div className="flex flex-wrap items-center justify-center gap-6 text-zinc-600 text-xs tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }} {...editableAttrs("home.brands.highlights", "array")}>
          {section.highlights.map((label, index) => (
            <span key={`${label}-${index}`} className="flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-red-600 inline-block" />
              {label}
            </span>
          ))}
        </div>
      </div>

      <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-zinc-700 to-transparent" />
    </section>
  );
};

export default BrandsSection;
